import React, { useState } from 'react';
import { Field } from 'react-final-form';

import { useIntl } from '../../util/reactIntl';
import {
  formatDateOfBirth,
  getDaysInMonth,
  getMaximumDateOfBirth,
  parseDateOfBirth,
} from '../../util/dateOfBirth';

import ValidationError from '../ValidationError/ValidationError';

import css from './FieldDateOfBirth.module.css';

const MONTH_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

const DateOfBirthSelects = props => {
  const { id, input, meta, today } = props;
  const intl = useIntl();
  const initialDate = parseDateOfBirth(input.value);
  const [year, setYear] = useState(initialDate.year);
  const [month, setMonth] = useState(initialDate.month);
  const [day, setDay] = useState(initialDate.day);
  const maximum = getMaximumDateOfBirth(today);

  const years = Array.from({ length: maximum.year - 1899 }, (_, index) => maximum.year - index);
  const maximumMonth = Number(year) === maximum.year ? maximum.month : 12;
  const months = MONTH_KEYS.slice(0, maximumMonth);
  const naturalMaximumDay = getDaysInMonth(Number(year), Number(month));
  const maximumDay =
    Number(year) === maximum.year && Number(month) === maximum.month
      ? Math.min(naturalMaximumDay, maximum.day)
      : naturalMaximumDay;
  const days = Array.from({ length: maximumDay }, (_, index) => index + 1);

  const publishValue = (nextYear, nextMonth, nextDay) => {
    if (nextYear && nextMonth && nextDay) {
      input.onChange(formatDateOfBirth(nextYear, nextMonth, nextDay));
    } else {
      input.onChange('');
    }
  };

  const handleYearChange = event => {
    const nextYear = Number(event.target.value) || '';
    const nextMaximumMonth = nextYear === maximum.year ? maximum.month : 12;
    const nextMonth = Number(month) > nextMaximumMonth ? '' : month;
    const nextNaturalMaximumDay = getDaysInMonth(nextYear, Number(nextMonth));
    const nextMaximumDay =
      nextYear === maximum.year && Number(nextMonth) === maximum.month
        ? Math.min(nextNaturalMaximumDay, maximum.day)
        : nextNaturalMaximumDay;
    const nextDay = Number(day) > nextMaximumDay ? '' : day;
    setYear(nextYear);
    setMonth(nextMonth);
    setDay(nextDay);
    publishValue(nextYear, nextMonth, nextDay);
  };

  const handleMonthChange = event => {
    const nextMonth = Number(event.target.value) || '';
    const nextNaturalMaximumDay = getDaysInMonth(Number(year), nextMonth);
    const nextMaximumDay =
      Number(year) === maximum.year && nextMonth === maximum.month
        ? Math.min(nextNaturalMaximumDay, maximum.day)
        : nextNaturalMaximumDay;
    const nextDay = Number(day) > nextMaximumDay ? '' : day;
    setMonth(nextMonth);
    setDay(nextDay);
    publishValue(year, nextMonth, nextDay);
  };

  const handleDayChange = event => {
    const nextDay = Number(event.target.value) || '';
    setDay(nextDay);
    publishValue(year, month, nextDay);
  };

  const label = intl.formatMessage({ id: 'FieldDateOfBirth.label' });
  const help = intl.formatMessage({ id: 'FieldDateOfBirth.help' });
  const dayPlaceholder = intl.formatMessage({ id: 'FieldDateOfBirth.day' });
  const monthPlaceholder = intl.formatMessage({ id: 'FieldDateOfBirth.month' });
  const yearPlaceholder = intl.formatMessage({ id: 'FieldDateOfBirth.year' });

  return (
    <fieldset className={css.root} aria-describedby={`${id}-help`}>
      <legend className={css.label}>{label}</legend>
      <div className={css.selects}>
        <select
          id={`${id}-day`}
          className={css.day}
          aria-label={dayPlaceholder}
          value={day}
          onChange={handleDayChange}
          onBlur={input.onBlur}
        >
          <option value="">{dayPlaceholder}</option>
          {days.map(value => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <select
          id={`${id}-month`}
          className={css.month}
          aria-label={monthPlaceholder}
          value={month}
          onChange={handleMonthChange}
          onBlur={input.onBlur}
        >
          <option value="">{monthPlaceholder}</option>
          {months.map((key, index) => (
            <option key={key} value={index + 1}>
              {intl.formatMessage({ id: `FieldDateOfBirth.month.${key}` })}
            </option>
          ))}
        </select>
        <select
          id={`${id}-year`}
          className={css.year}
          aria-label={yearPlaceholder}
          value={year}
          onChange={handleYearChange}
          onBlur={input.onBlur}
        >
          <option value="">{yearPlaceholder}</option>
          {years.map(value => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </div>
      <p id={`${id}-help`} className={css.help}>
        {help}
      </p>
      <ValidationError fieldMeta={meta} />
    </fieldset>
  );
};

const FieldDateOfBirth = props => <Field component={DateOfBirthSelects} {...props} />;

export default FieldDateOfBirth;
